import { ReactNode } from 'react'

import { ResponsiveStyleValue, SxProps } from '@mui/system'

import { CommonTableColumnsEnum } from '../../components/Table/types'
import { LoadingStatusEnum } from '../../store/types'
import { PagePagination, SortDirectionCapital } from '../../types'
import { TradeAiAnalysis } from '../TradingLog/types'

export enum smallWidgetTypes {
  money = 'money',
  percents = 'percents',
  time = 'time',
  number = 'number',
}

export enum SmallWidgetsName {
  grossProfit = 'grossProfit',
  netProfit = 'netProfit',
  balance = 'balance',
  roi = 'roi',
  avgTradeSize = 'avgTradeSize',
  avgHoldingTime = 'avgHoldingTime',
  toxicity = 'toxicity',
  systemQualityNumber = 'systemQualityNumber',
}

export enum MediumWidgetsName {
  targetAccuracy = 'targetAccuracy',
  avgWinnerLoser = 'avgWinnerLoser',
  biggestWinnerLoser = 'biggestWinnerLoser',
  longShort = 'longShort',
}

export enum BigWidgetsName {
  profitFactor = 'profitFactor',
  topPerformance = 'topPerformance',
  winRatio = 'winRatio',
  riskRewardRatio = 'riskRewardRatio',
  commissionCompare = 'commissionCompare',
}

export type Position<T> = {
  data: T[]
  pagination: PagePagination<T>
}

export type DashboardState = {
  customize: boolean
  widgetsData: DashboardMetricsDTO
  smallWidgetsList: Record<SmallWidgetsName, boolean>
  mediumWidgetsList: Record<MediumWidgetsName, boolean>
  bigWidgetsList: Record<BigWidgetsName, boolean>
  positions: { open: Position<OpenPositions>; closed: Position<ClosedPositions> }
  // refreshTrigger: number
  loading: LoadingStatusEnum
  widgetsLoading: LoadingStatusEnum
}

type WidgetPropsType = {
  explanation: string | ReactNode
  children?: ReactNode
  label: string
}

export type SmallWidgetPropsType = WidgetPropsType & {
  name?: SmallWidgetsName
  mainInfo: string | number | Date
  type: smallWidgetTypes
  currency?: string
  isLoading?: boolean
  sx?: SxProps
}

export type WidgetWrapperType = WidgetPropsType & {
  isChecked?: boolean
  handleCheck?: () => void
  minWidth?: ResponsiveStyleValue<string | number>
  sx?: SxProps
}

export type SmallWidgetItem = {
  id: SmallWidgetsName
}

export type MediumWidgetItem = {
  id: MediumWidgetsName
}

export type BigWidgetItem = {
  id: BigWidgetsName
}

export type MoneyValueProps = {
  currency?: string | null
  money: string | number
  bigFont?: boolean
  isLoading?: boolean
}

export type OpenPositions = {
  [CommonTableColumnsEnum.openTime]: string
  [CommonTableColumnsEnum.symbol]: string
  [CommonTableColumnsEnum.volume]: number
  [CommonTableColumnsEnum.posValue]: number
  tradeAiAnalysis?: TradeAiAnalysis | null
}

export type ClosedPositions = {
  [CommonTableColumnsEnum.openTime]: string
  [CommonTableColumnsEnum.closeTime]: string | null
  [CommonTableColumnsEnum.symbol]: string
  [CommonTableColumnsEnum.volume]: number
  [CommonTableColumnsEnum.posValue]: number
  tradeAiAnalysis?: TradeAiAnalysis | null
}

export type PositionsTablesDTO<T> = {
  data: T[]
  meta: {
    page: number
    perPage: number
    itemCount: number
    pageCount: number
    hasPreviousPage: boolean
    hasNextPage: boolean
  }
}

export type DashboardRequestType = {
  account: number
  period: (string | null)[]
}

export type PositionsRequestType = {
  order: SortDirectionCapital
  orderBy: keyof OpenPositions | keyof ClosedPositions | CommonTableColumnsEnum.id | null
  page: number
  perPage: number
  account: number
  period: (string | null)[]
}

export type ProfitFactorType = {
  grossProfit: number
  grossLoss: number
  factor: number
}

export type WinnerLooserType = {
  winner: number
  looser: number
}

export type TopPerformanceType = {
  leastProfitableSession: string
  mostProfitableSession: string
  mostActiveSession: string
}

export type WinRatioType = {
  winningTrades: number
  loosingTrades: number
  perCent: number
}

export type LongShortPositionsType = {
  long: number
  short: number
}

export type TargetAccuracyType = {
  stopLoss: number
  takeProfit: number
}

export type toxicityWidgetType = {
  toxicityPercent: number
  amountTransactions: number
}

export type riskRewardRatioWidgetType = {
  riskRewardRatioPercent: number
  potentialProfit: number
  potentialLoss: number
}

export type DashboardMetricsDTO = {
  grossProfit: number
  netProfit: number
  profitFactor: ProfitFactorType
  averageWinnerLooser: WinnerLooserType
  biggerWinnerLooser: WinnerLooserType
  topPerformance: TopPerformanceType
  winRatio: WinRatioType
  averageTradeSize: number
  returnOnInvestment: number | string
  toxicity: toxicityWidgetType
  longShortPositions: LongShortPositionsType
  averageHoldingTime: string
  targetAccuracy: TargetAccuracyType
  riskRewardRatio: riskRewardRatioWidgetType
  systemQualityNumber: number
}

export type CalendarCellDataType = {
  count: number
  sum: number
  date: string
}
